import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { api, type Response } from '@/lib/api'
import type { SelectOption } from '@/components/SearchableSelect'

/**
 * The lookup lists behind the Profile Form's dropdowns, plus the reads that
 * pre-fill it with whatever the candidate has already saved.
 *
 * The original fetched each list from its own `*.js` section file, re-created a
 * select2 widget per fetch, and re-fetched on every page load. They are cached
 * here — this data changes about as often as the org chart.
 */
const LOOKUP_STALE = 10 * 60 * 1000

function useLookup<T>(key: string, path: string): UseQueryResult<Response<T[]>> {
  return useQuery({
    queryKey: ['masterData', key],
    queryFn: () => api.get<Response<T[]>>(path),
    staleTime: LOOKUP_STALE,
  })
}

export interface MstProvince {
  ProvinceID: number
  ProvinceName: string | null
}
export interface MstCity {
  City_HCIS_ID: number
  CityName: string | null
}
export interface MstCountry {
  Country_HCIS_Code: string | null
  CountryName: string | null
}
export interface MstReligion {
  ReligionCode: string | null
  ReligionName: string | null
}
export interface MstDegree {
  mstDegreeID: number
  DegreeName: string | null
}
export interface MstUniversity {
  mstUniversityID: number
  UniversityName: string | null
}
export interface MstMajor {
  mstMajorID: number
  MajorName: string | null
}
export interface MstIndustry {
  mstIndustryID: number
  IndustryName: string | null
}
export interface MstWorkLevel {
  mstWorkExperienceLevelID: number
  WorkExperienceLevelName: string | null
}
export interface MstOrganizationType {
  mstOrganizationTypeID: number
  OrganizationType: string | null
}

export const useProvinces = () => useLookup<MstProvince>('province', '/api/ecareer/province/list')
export const useCities = () => useLookup<MstCity>('city', '/api/ecareer/city/list')
export const useCountries = () => useLookup<MstCountry>('country', '/api/ecareer/country/list')
export const useReligions = () => useLookup<MstReligion>('religion', '/api/ecareer/religion/list')
export const useDegrees = () => useLookup<MstDegree>('degree', '/api/ecareer/degree/list')
export const useUniversities = () =>
  useLookup<MstUniversity>('university', '/api/ecareer/university/list')
export const useMajors = () => useLookup<MstMajor>('major', '/api/ecareer/major/list')
export const useIndustries = () => useLookup<MstIndustry>('industry', '/api/ecareer/industry/list')
export const useWorkLevels = () =>
  useLookup<MstWorkLevel>('workExperienceLevel', '/api/ecareer/workExperienceLevel/list')
export const useOrganizationTypes = () =>
  useLookup<MstOrganizationType>('organizationType', '/api/ecareer/organization/list')

/** Maps a lookup list to dropdown options. */
export function optionsFrom<T>(
  data: T[] | null | undefined,
  value: (item: T) => string | number | null,
  label: (item: T) => string | null,
): SelectOption[] {
  return (data ?? [])
    .filter((item) => value(item) !== null && value(item) !== undefined)
    .map((item) => ({ value: String(value(item)), label: label(item) ?? '' }))
}

/* ------------------------- existing profile ------------------------- */

/**
 * The candidate's saved profile, if any. All five reads are session-scoped —
 * the email comes from the cookie, never from the client.
 */
export interface SavedPersonalInformation {
  trxPersonalInformationID?: number | null
  FullName: string | null
  Email: string | null
  PlaceOfBirth: string | null
  DateOfBirth: string | null
  Age: number | null
  Domicile: string | null
  Country_HCIS_Code: string | null
  ReligionCode: string | null
  City_HCIS_ID: string | null
  State_HCIS_ID: string | null
  Address: string | null
  JobPreferences: string | null
  Gender: number | null
  PhoneNumber: string | null
  Instagram: string | null
  LinkedIn: string | null
  mstMaritalStatusID: number | null
  PhotoPath: string | null
  CurriculumVitaePath: string | null
}

export interface SavedEducation {
  trxCandidateEducationID: number
  mstDegreeID: number
  DegreeName: string | null
  mstUniversityID: number
  UniversityName: string | null
  UniversityOther: string | null
  mstMajorID: number
  MajorName: string | null
  MajorOther: string | null
  GPA: number
  MonthIn: number
  YearIn: number
  MonthOut: number
  YearOut: number
  isUntilNow: boolean
}

export interface SavedWorkExperience {
  trxCandidateWorkExperienceID: number
  Company: string | null
  mstIndustryID: number
  IndustryName: string | null
  Position: string | null
  mstWorkExperienceLevelID: number
  WorkExperienceLevelName: string | null
  MonthIn: number
  YearIn: number
  MonthOut: number
  YearOut: number
  isUntilNow: boolean
  Salary: number
  JobDescription: string | null
}

export interface SavedOrganization {
  trxCandidateOrganizationExperienceID: number
  OrganizationName: string | null
  mstOrganizationTypeID: number
  OrganizationType: string | null
  OrganizationPosition: string | null
}

export interface SavedAchievement {
  IsHaveAchievement: boolean | null
  AchievementDescription: string | null
  SoftSkillDescription: string | null
  TechnicalSkill: string | null
  CertificationPath: string | null
}

export interface SavedFamily {
  IsHaveFamilyInTBG: boolean | null
  FamilyInTBG: string | null
  IsHaveFamilyInTelcoIndustry: boolean | null
  FamilyInTelcoIndustry: string | null
}

export interface SavedMedical {
  IsHaveMedicalHistory: boolean | null
  MedicalHistory: string | null
}

export const useSavedProfile = () =>
  useQuery({
    queryKey: ['profile', 'info'],
    queryFn: () => api.get<Response<SavedPersonalInformation>>('/api/ecareer/profile/info'),
    retry: false,
  })

/**
 * The candidate's photo, base64-encoded.
 *
 * `PhotoPath` on the profile is a Windows UNC share, which the browser refuses
 * to load ("Not allowed to load local resource: file://..."). The bytes come
 * through this endpoint instead, exactly as `loadOPersonalPhoto` fetched them.
 */
export interface SavedPhoto {
  ImageBase64: string | null
}

export const useSavedPhoto = () =>
  useQuery({
    queryKey: ['profile', 'photo'],
    queryFn: () => api.get<Response<SavedPhoto>>('/api/ecareer/profile/info/photo'),
    retry: false,
  })

/** Turns the API's base64 into something an <img src> accepts. */
export function photoDataUrl(photo: SavedPhoto | null | undefined): string {
  const base64 = photo?.ImageBase64
  if (!base64) return ''
  // Some rows already carry the data: prefix; most do not.
  return base64.startsWith('data:image/') ? base64 : `data:image/jpeg;base64,${base64}`
}

export const useSavedEducation = () =>
  useQuery({
    queryKey: ['profile', 'education'],
    queryFn: () => api.get<Response<SavedEducation[]>>('/api/ecareer/profile/info/education'),
    retry: false,
  })

export const useSavedWorkExperience = () =>
  useQuery({
    queryKey: ['profile', 'workExperience'],
    queryFn: () =>
      api.get<Response<SavedWorkExperience[]>>('/api/ecareer/profile/info/workExperience'),
    retry: false,
  })

export const useSavedOrganization = () =>
  useQuery({
    queryKey: ['profile', 'organization'],
    queryFn: () => api.get<Response<SavedOrganization[]>>('/api/ecareer/profile/info/organization'),
    retry: false,
  })

export const useSavedAchievement = () =>
  useQuery({
    queryKey: ['profile', 'achievement'],
    queryFn: () => api.get<Response<SavedAchievement>>('/api/ecareer/profile/info/achievement'),
    retry: false,
  })

export const useSavedFamily = () =>
  useQuery({
    queryKey: ['profile', 'family'],
    queryFn: () => api.get<Response<SavedFamily>>('/api/ecareer/profile/info/family'),
    retry: false,
  })

export const useSavedMedical = () =>
  useQuery({
    queryKey: ['profile', 'medical'],
    queryFn: () => api.get<Response<SavedMedical>>('/api/ecareer/profile/info/medical'),
    retry: false,
  })
